import { useForm } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { UseValueSetExpandResult } from '../../hooks/useValueSetExpand';
import { DatePicker } from '../atoms/DatePicker';
import { Button } from '../ui/button';
import { Field, FieldGroup, FieldLabel } from '../ui/field';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Spinner } from '../ui/spinner';
import { ValueSetCombobox, type ValueSetCode } from './ValueSetCombobox';

// oxlint-disable-next-line react/only-export-components
export const IMMUNIZATION_VACCINE_CODE_VALUE_SET_URL = 'http://hl7.org/fhir/ValueSet/vaccine-code';
// oxlint-disable-next-line react/only-export-components
export const IMMUNIZATION_SITE_VALUE_SET_URL = 'http://hl7.org/fhir/ValueSet/immunization-site';
// oxlint-disable-next-line react/only-export-components
export const IMMUNIZATION_ROUTE_VALUE_SET_URL = 'http://hl7.org/fhir/ValueSet/immunization-route';

const IMMUNIZATION_STATUSES = ['completed', 'not-done'] as const;

const formSchema = z.object({
  vaccineCode: z.custom<ValueSetCode | undefined>().refine((v): v is ValueSetCode => v !== undefined),
  status: z.enum(IMMUNIZATION_STATUSES),
  occurrenceDate: z.string().min(1),
  lotNumber: z.union([z.string(), z.undefined()]),
  expirationDate: z.union([z.string(), z.undefined()]),
  site: z.custom<ValueSetCode | undefined>(),
  route: z.custom<ValueSetCode | undefined>(),
  doseQuantity: z.union([z.string(), z.undefined()]),
  doseUnit: z.union([z.string(), z.undefined()]),
  notes: z.union([z.string(), z.undefined()]),
});

const isRequired = (name: keyof typeof formSchema.shape): boolean => !formSchema.shape[name].isOptional();

export type AddImmunizationFormValues = z.infer<typeof formSchema>;

export interface AddImmunizationFormProps {
  onCreate: (values: AddImmunizationFormValues) => Promise<void>;
  onClose: () => void;
  isCreating: boolean;
  vaccineCode: UseValueSetExpandResult;
  site: UseValueSetExpandResult;
  route: UseValueSetExpandResult;
}

export function AddImmunizationForm({
  onCreate,
  onClose,
  isCreating,
  vaccineCode,
  site,
  route,
}: AddImmunizationFormProps): React.ReactNode {
  const { t } = useTranslation('records');

  const form = useForm({
    defaultValues: {
      vaccineCode: undefined as ValueSetCode | undefined,
      status: 'completed' as (typeof IMMUNIZATION_STATUSES)[number],
      occurrenceDate: '',
      lotNumber: undefined as string | undefined,
      expirationDate: undefined as string | undefined,
      site: undefined as ValueSetCode | undefined,
      route: undefined as ValueSetCode | undefined,
      doseQuantity: undefined as string | undefined,
      doseUnit: undefined as string | undefined,
      notes: undefined as string | undefined,
    },
    validators: { onChange: formSchema, onMount: formSchema, onSubmit: formSchema },
    onSubmit: async ({ value }) => {
      await onCreate(formSchema.parse(value));
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
      className="space-y-3"
    >
      <FieldGroup className="max-h-[80vh] overflow-y-auto px-4">
        <form.Field name="vaccineCode">
          {(field) => (
            <ValueSetCombobox
              id={field.name}
              label={t('vaccineCode')}
              required={isRequired('vaccineCode')}
              placeholder={t('vaccineCodePlaceholder')}
              items={vaccineCode.items}
              value={field.state.value ?? null}
              onValueChange={(value) => field.handleChange(value ?? undefined)}
              onQueryChange={vaccineCode.onQueryChange}
              isLoading={vaccineCode.isFetching}
              error={vaccineCode.error}
              emptyMessage={t('noMatchingResults')}
            />
          )}
        </form.Field>

        <form.Field name="status">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>
                {t('status')}
                {isRequired('status') && <span aria-hidden="true">*</span>}
              </FieldLabel>
              <Select
                value={field.state.value}
                onValueChange={(v) => field.handleChange(v as (typeof IMMUNIZATION_STATUSES)[number])}
              >
                <SelectTrigger id={field.name} aria-label={t('status')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completed">{t('immunizationStatusCompleted')}</SelectItem>
                  <SelectItem value="not-done">{t('immunizationStatusNotDone')}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          )}
        </form.Field>

        <form.Field name="occurrenceDate">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>
                {t('occurrenceDate')}
                {isRequired('occurrenceDate') && <span aria-hidden="true">*</span>}
              </FieldLabel>
              <DatePicker
                id={field.name}
                required={isRequired('occurrenceDate')}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(value) => field.handleChange(value)}
                aria-label={t('occurrenceDate')}
              />
            </Field>
          )}
        </form.Field>

        <form.Field name="lotNumber">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('lotNumber')}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value ?? ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-label={t('lotNumber')}
              />
            </Field>
          )}
        </form.Field>

        <form.Field name="expirationDate">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('expirationDate')}</FieldLabel>
              <DatePicker
                id={field.name}
                value={field.state.value ?? ''}
                onBlur={field.handleBlur}
                onChange={(value) => field.handleChange(value)}
                aria-label={t('expirationDate')}
              />
            </Field>
          )}
        </form.Field>

        <form.Field name="site">
          {(field) => (
            <ValueSetCombobox
              id={field.name}
              label={t('site')}
              placeholder={t('sitePlaceholder')}
              items={site.items}
              value={field.state.value ?? null}
              onValueChange={(value) => field.handleChange(value ?? undefined)}
              onQueryChange={site.onQueryChange}
              isLoading={site.isFetching}
              error={site.error}
              emptyMessage={t('noMatchingResults')}
            />
          )}
        </form.Field>

        <form.Field name="route">
          {(field) => (
            <ValueSetCombobox
              id={field.name}
              label={t('route')}
              placeholder={t('routePlaceholder')}
              items={route.items}
              value={field.state.value ?? null}
              onValueChange={(value) => field.handleChange(value ?? undefined)}
              onQueryChange={route.onQueryChange}
              isLoading={route.isFetching}
              error={route.error}
              emptyMessage={t('noMatchingResults')}
            />
          )}
        </form.Field>

        <div className="flex gap-2">
          <form.Field name="doseQuantity">
            {(field) => (
              <Field className="flex-1">
                <FieldLabel htmlFor={field.name}>{t('doseQuantity')}</FieldLabel>
                <Input
                  id={field.name}
                  name={field.name}
                  type="number"
                  min="0"
                  step="0.01"
                  value={field.state.value ?? ''}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  aria-label={t('doseQuantity')}
                />
              </Field>
            )}
          </form.Field>

          <form.Field name="doseUnit">
            {(field) => (
              <Field className="flex-1">
                <FieldLabel htmlFor={field.name}>{t('doseUnit')}</FieldLabel>
                <Input
                  id={field.name}
                  name={field.name}
                  value={field.state.value ?? ''}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder={t('doseUnitPlaceholder')}
                  aria-label={t('doseUnit')}
                />
              </Field>
            )}
          </form.Field>
        </div>

        <form.Field name="notes">
          {(field) => (
            <Field>
              <FieldLabel htmlFor={field.name}>{t('notes')}</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                value={field.state.value ?? ''}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                placeholder={t('notePlaceholder')}
                aria-label={t('notes')}
              />
            </Field>
          )}
        </form.Field>
      </FieldGroup>

      <div className="flex gap-2">
        <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting] as const}>
          {([canSubmit, isSubmitting]) => (
            <Button
              type="submit"
              size="sm"
              disabled={!canSubmit || isSubmitting || isCreating}
              className="flex-1"
              aria-label={t('saveNewImmunization')}
            >
              {isSubmitting || isCreating ? <Spinner className="size-4" /> : t('add')}
            </Button>
          )}
        </form.Subscribe>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onClose}
          disabled={isCreating}
          className="flex-1"
          aria-label={t('cancelAddImmunization')}
        >
          {t('cancel')}
        </Button>
      </div>
    </form>
  );
}
